import { Button, makeStyles } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import ProjectManagementComponentsTable from "./ProjectManagementComponentsTable";
import ProjectManagementFields from "./ProjectManagementFields";
import {
  getPMFieldSettings,
  updatePMFieldSettings,
} from "../../../actions/admin-actions";
import Loading from "../../../common-components/Loading";
import ActionMessages from "../../../common-components/ActionMessages";
import { getResponse } from "../../../shared-resources/utils";

const useStyles = makeStyles((theme) => ({
  updateBtn: {
    fontSize: "14px",
    padding: "4px 12px !important",
    textTransform: "none",
    borderRadius: "20px",
    background: "#0070D2",
  },
}));

export default function ProjectManagement() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const pmFieldSettingsData = useSelector(
    (state) => state.adminReducer.pmFieldSettingsData
  );

  const updatePMFieldSettingsData = useSelector(
    (state) => state.adminReducer.updatePMFieldSettingsData
  );

  const [loading, setLoading] = useState(true);
  const [emptyErrMsg, setEmptyErrMsg] = useState(false);
  const [msgType, setMsgType] = useState();
  const [msgText, setMsgText] = useState();
  const [componentsSettingsData, setComponentsSettingsData] = useState([]);
  const [fieldsSettingsData, setFieldsSettingsData] = useState([]);
  const [buttonsSettingsData, setButtonsSettingsData] = useState([]);
  const [updateData, setUpdateData] = useState([]);
  const [isUpdateEnable, setIsUpdateEnable] = useState(false);
  const [isUpdateClick, setIsUpdateClick] = useState(false);

  const compare = (a, b) => {
    if (a.field_id < b.field_id) {
      return -1;
    }
    if (a.field_id > b.field_id) {
      return 1;
    }
    return 0;
  };

  const isClosed = (val) => {
    setEmptyErrMsg(!val);
  };

  useEffect(() => {
    dispatch(getPMFieldSettings());
  }, []);

  useEffect(() => {
    if (
      updatePMFieldSettingsData &&
      updatePMFieldSettingsData.status &&
      updatePMFieldSettingsData.status === 200 &&
      isUpdateClick
    ) {
      setLoading(false);
      setEmptyErrMsg(true);
      setMsgType("success");
      setMsgText("Settings updated successfully");
      setIsUpdateEnable(false);
      setIsUpdateClick(false);
      dispatch(getPMFieldSettings());
    }
    if (
      updatePMFieldSettingsData &&
      updatePMFieldSettingsData.status &&
      updatePMFieldSettingsData.status === 400 &&
      isUpdateClick
    ) {
      setLoading(false);
      setEmptyErrMsg(true);
      setMsgType("error");
      setMsgText(updatePMFieldSettingsData.message);
      setIsUpdateClick(false);
    }
  }, [updatePMFieldSettingsData]);

  useEffect(() => {
    if (
      pmFieldSettingsData &&
      pmFieldSettingsData.status &&
      (pmFieldSettingsData.status === 200 || pmFieldSettingsData.status === 400)
    ) {
      setLoading(false);
      const data = getResponse(pmFieldSettingsData);
      if (data) {
        setComponentsSettingsData(data.componentSettings.sort(compare));
        setFieldsSettingsData(data.fieldSettings.sort(compare));
        setButtonsSettingsData(data.buttonSettings.sort(compare));
        setUpdateData(data);
      }
    }
  }, [pmFieldSettingsData]);

  const getPicklistLength = (items, value) => {
    return items.filter(
      (val) =>
        val.name.toLowerCase().trim().replace(/\s\s+/g, " ") ===
        value.toLowerCase().trim().replace(/\s\s+/g, " ")
    ).length;
  };

  const onUpdateClick = () => {
    let isValid = true;
    let isNotDuplicate = true;
    let emptyFields = [];
    updateData.fieldSettings.forEach((val) => {
      if (val.adminPicklistSettingsList && isValid) {
        val.adminPicklistSettingsList.forEach((item) => {
          if (item.name.toString().trim() === "") {
            isValid = false;
            emptyFields.push(val.fields);
          }
        });
      }
      if (
        val.setting_type === 3 &&
        (val.field_type === "Picklist" ||
          val.field_type === "Picklist-Multiselect") &&
        val.adminPicklistSettingsList.length === 0
      ) {
        isValid = false;
        emptyFields.push(val.fields);
      }
      if (val.adminPicklistSettingsList && isNotDuplicate) {
        val.adminPicklistSettingsList.forEach((item) => {
          isNotDuplicate =
            getPicklistLength(val.adminPicklistSettingsList, item.name) > 1
              ? false
              : true;
        });
      }
    });
    if (!isValid) {
      setEmptyErrMsg(true);
      setMsgType("error");
      setMsgText(
        `Field type Picklist/Picklist-Multiselect does not have any picklist values: ${emptyFields.join(
          ", "
        )}`
      );
    } else if (!isNotDuplicate) {
      setEmptyErrMsg(true);
      setMsgType("error");
      setMsgText("Duplicate picklist values are not allowed");
    } else {
      setLoading(true);
      setIsUpdateClick(true);
      dispatch(updatePMFieldSettings(updateData));
    }
  };

  const removeEditFlag = (data) => {
    const changeData = JSON.parse(JSON.stringify(data));
    changeData.forEach((item) => {
      item.adminPicklistSettingsList.forEach((val) => {
        delete val.isEditable;
      });
    });
    return changeData;
  };

  const changeComponentsTable = (data) => {
    setUpdateData({ ...updateData, componentSettings: data });
    setIsUpdateEnable(true);
  };

  const changePrimaryTable = (data) => {
    const excludeFlag = removeEditFlag(JSON.parse(JSON.stringify(data)));
    setUpdateData({ ...updateData, fieldSettings: excludeFlag });
    setIsUpdateEnable(true);
  };

  const changeSecondaryable = (data) => {
    setUpdateData({ ...updateData, buttonSettings: data });
    setIsUpdateEnable(true);
  };

  return (
    <>
      <div style={{ textAlign: "right", marginBottom: "12px" }}>
        <Button
          size="small"
          color="primary"
          variant="contained"
          disableElevation
          className={classes.updateBtn}
          onClick={onUpdateClick}
          disabled={!isUpdateEnable}
        >
          Update
        </Button>
      </div>
      <ProjectManagementComponentsTable
        data={componentsSettingsData}
        changeComponentsTable={changeComponentsTable}
      />
      <ProjectManagementFields
        projectManagementPermissions={fieldsSettingsData}
        projectManagementPermissionsSecondary={buttonsSettingsData}
        changePrimaryTable={changePrimaryTable}
        changeSecondaryable={changeSecondaryable}
      />
      {loading && <Loading />}
      {emptyErrMsg && (
        <ActionMessages type={msgType} isClosed={isClosed} message={msgText} />
      )}
    </>
  );
}
